import { FORM_DISPLAY_NAMES } from './formSectionConstants';

enum FormSaveType {
  DRAFT = 'DRAFT',
  DELIVERED = 'DELIVERED',
  REVIEWED = 'REVIEWED',
}

enum CapFormAutoFilledQuestions {
  CAP_POLICY_APPLICATION = 'CAP_POLICY_APPLICATION',
  RSPR_UTILIZATION = 'RSPR_UTILIZATION',
  RSPR_POLICY_RECOMMENDATION = 'RSPR_POLICY_RECOMMENDATION',
}

enum StepperProgressTypes {
  PAST = 'past',
  PRESENT = 'present',
  FUTURE = 'future',
}

enum FormComponents {
  DROPDOWN = 'DROPDOWN',
  RADIO = 'RADIO',
  FREETEXT = 'FREETEXT',
  TITLE = 'TITLE',
  AUTOFILL = 'AUTOFILL',
  METBAR = 'METBAR',
}

// ============================================
// FORM IDENTIFIERS
// ============================================
export const FORM_IDS = {
  DART_ESCALATIONS: 'DART_ESCALATIONS_FORM',
  DART_CONSULTATION: 'DART_CONSULTATION_FORM',
  TRANSACTION_MONITORING: 'TRANSACTION_MONITORING_FORM',
  D2AS_CONTACT_REVIEW: 'D2AS_CONTACT_REVIEW_FORM',
} as const;

export type FormId = (typeof FORM_IDS)[keyof typeof FORM_IDS];

// ============================================
// SECTION IDENTIFIERS
// ============================================
export const SECTION_IDS = {
  DART_DROPDOWNS: 'DART_DROPDOWNS',
  DART_MANAGER_SUMMARY: 'DART_MANAGER_SUMMARY',
  TX_MONITORING_DROPDOWNS: 'TX_MONITORING_DROPDOWNS',
  D2_MONITORING_DROPDOWNS: 'D2_MONITORING_DROPDOWNS',
} as const;

// ============================================
// QUESTION IDENTIFIERS
// ============================================
export const QUESTION_IDS = {
  MANAGER_SUMMARY: 'MANAGER_SUMMARY',
  CONTACT_REVIEW_FORM_TYPE: 'CONTACT_REVIEW_FORM_TYPE',
  VERTICAL: 'VERTICAL',
  DART_ESCALATIONS_VERTICAL: 'DART_ESCALATIONS_VERTICAL',
  DART_CONSULTATION_VERTICAL: 'DART_CONSULTATION_VERTICAL',
} as const;

export const SECTION_NAME = {
  TABLE: 'TABLE',
  TABLE_HEADER: 'TABLE_HEADER',
};

// ============================================
// DROPDOWN TYPES
// ============================================
export const DROPDOWN_TYPES = {
  VERTICAL: 'vertical',
  CONTACT_REVIEW_FORM_TYPE: 'contact_review_form_type',
  DART_ESCALATIONS_VERTICAL: 'dart_escalations_vertical',
  DART_CONSULTATION_VERTICAL: 'dart_consultation_vertical',
} as const;

// ============================================
// INGRESS SOURCES
// ============================================
export const INGRESS_SOURCES = {
  OUTLIER: 'outlierManagement',
  REALTIME_HMD: 'realtimeHMD',
  CCX: 'ccx',
} as const;

export type IngressSource =
  (typeof INGRESS_SOURCES)[keyof typeof INGRESS_SOURCES];

// ============================================
// ANSWER VALUES
// ============================================
export const DART_ANSWER_VALUES = {
  MEETS_BAR: 0,
  DOESNT_MEET_BAR: 1,
  NOT_APPLICABLE: 2,
} as const;

export const TX_MONITORING_ANSWER_VALUES = {
  MET_THE_BAR: true,
  BELOW_THE_BAR: false,
} as const;

// ============================================
// TRANSLATION IDS
// ============================================
export const TRANSLATION_IDS = {
  DART_MEETS_BAR: 'cs_perfportal_dart_meets_bar',
  DART_DOESNT_MEET_BAR: 'cs_perfportal_dart_doesnt_meet_bar',
  DART_NOT_APPLICABLE: 'cs_perfportal_dart_not_applicable',
  TX_MET_BAR: 'cs_perfportal_contactReview_barRaisedQuestion_metTheBar',
  TX_BELOW_BAR: 'cs_perfportal_barRaisedQuestion_belowTheBar',
  DART_ESCALATIONS_TITLE: 'cs_perfportal_dart_escalations_form_title',
  DART_CONSULTATION_TITLE: 'cs_perfportal_dart_consultation_form_title',
} as const;

export const TRANSLATION_IDS_SUBSTRING = {
  MEETS_BAR: 'meets_bar',
  DOESNT_MEET_BAR: 'doesnt_meet_bar',
  NOT_APPLICABLE: 'not_applicable',
};

// ============================================
// UTILITY FUNCTIONS
// ============================================
export const isOutlierIngress = (ingress: string | null): boolean => {
  return ingress === INGRESS_SOURCES.OUTLIER;
};

export const isRealtimeHMDIngress = (ingress: string | null): boolean => {
  return ingress === INGRESS_SOURCES.REALTIME_HMD;
};

export const getDartAnswerValue = (
  checkedValue: number
): { defaultMessage: string; translationID: string } => {
  switch (checkedValue) {
    case DART_ANSWER_VALUES.MEETS_BAR:
      return {
        defaultMessage: 'Meets the bar',
        translationID: TRANSLATION_IDS.DART_MEETS_BAR,
      };
    case DART_ANSWER_VALUES.DOESNT_MEET_BAR:
      return {
        defaultMessage: "Doesn't meet the bar",
        translationID: TRANSLATION_IDS.DART_DOESNT_MEET_BAR,
      };
    case DART_ANSWER_VALUES.NOT_APPLICABLE:
      return {
        defaultMessage: 'Not applicable',
        translationID: TRANSLATION_IDS.DART_NOT_APPLICABLE,
      };
    default:
      throw new Error(`Invalid DART answer value: ${checkedValue}`);
  }
};

export const TOAST_MESSAGES = {
  DRAFT_SUCCESS: 'Draft saved successfully',
  ERROR_SAVING: 'An error occurred while saving the form',
  ERROR_SUBMITTING: 'An error occurred while submitting the form',
  FORM_TEMPLATE_FAILED: 'Failed to load form template',
  CONTACTS_FAILED: 'Failed to load contacts',
};

export const getVerticalTypeForForm = (formType: string): string => {
  return formType === FORM_DISPLAY_NAMES.DART_ESCALATIONS_FORM
    ? DROPDOWN_TYPES.DART_ESCALATIONS_VERTICAL
    : DROPDOWN_TYPES.DART_CONSULTATION_VERTICAL;
};

export {
  FormSaveType,
  StepperProgressTypes,
  FormComponents,
  CapFormAutoFilledQuestions,
};
